"use client";

import { useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { useTheme as useNextTheme } from "next-themes";
import { appColorsDark, appColorsLight } from "@/styles/colors/palette";

type MuiThemeProviderProps = {
  children: React.ReactNode;
};

const buildTheme = (mode: "light" | "dark") => {
  const appColors = mode === "dark" ? appColorsDark : appColorsLight;
  return createTheme({
    palette: {
      mode,
      background: {
        default: "hsl(var(--background))",
        paper: "hsl(var(--card))",
      },
      text: {
        primary: "hsl(var(--foreground))",
        secondary: "hsl(var(--muted-foreground))",
      },
      primary: {
        main: "hsl(var(--primary))",
        contrastText: "hsl(var(--primary-foreground))",
      },
      secondary: {
        main: "hsl(var(--secondary))",
        contrastText: "hsl(var(--secondary-foreground))",
      },
      warning: {
        main: appColors.semantic.warning,
        dark: appColors.semantic.warningStrong,
        contrastText: appColors.semantic.white,
      },
      error: {
        main: "hsl(var(--destructive))",
        contrastText: "hsl(var(--destructive-foreground))",
      },
      divider: "hsl(var(--border))",
    },
    typography: {
      fontFamily: "var(--font-sans)",
      h1: { fontFamily: "var(--font-display)", fontWeight: 700 },
      h2: { fontFamily: "var(--font-display)", fontWeight: 700 },
      h3: { fontFamily: "var(--font-display)", fontWeight: 700 },
      h4: { fontFamily: "var(--font-display)", fontWeight: 700 },
      h5: { fontFamily: "var(--font-display)", fontWeight: 700 },
      h6: { fontFamily: "var(--font-display)", fontWeight: 700 },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderColor: "hsl(var(--border))",
          },
          outlined: {
            borderColor: "hsl(var(--border))",
            backgroundColor: "hsl(var(--card))",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: "1px solid hsl(var(--border))",
            backgroundColor: "hsl(var(--popover))",
            boxShadow: "0 16px 36px hsl(var(--foreground) / 0.2)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
            "&:hover, &.Mui-focusVisible": {
              backgroundColor: "hsl(var(--primary) / 0.9)",
              color: "hsl(var(--primary-foreground))",
            },
          },
          contained: {
            boxShadow: "0 10px 24px hsl(var(--primary) / 0.28)",
            "&:hover": {
              boxShadow: "0 12px 26px hsl(var(--primary) / 0.36)",
            },
          },
          containedWarning: {
            boxShadow: `0 10px 24px ${appColors.semantic.warning}33`,
            "&:hover": {
              boxShadow: `0 12px 26px ${appColors.semantic.warningStrong}40`,
            },
          },
          outlined: {
            borderColor: "hsl(var(--border))",
            "&:hover": {
              borderColor: "hsl(var(--primary))",
              backgroundColor: "hsl(var(--muted) / 0.45)",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "hsl(var(--primary))",
          },
        },
      },
    },
  });
};

export function MuiThemeProvider({ children }: MuiThemeProviderProps) {
  const { theme: themePreference, resolvedTheme, systemTheme } = useNextTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Keep SSR and first client render deterministic to avoid hydration mismatch.
  // When theme=system, prefer systemTheme explicitly to react to OS changes.
  const activeTheme =
    themePreference === "system"
      ? (systemTheme ?? resolvedTheme)
      : resolvedTheme;
  const mode = mounted && activeTheme === "dark" ? "dark" : "light";
  const muiTheme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
}
